import { FieldArray, Form, Formik } from 'formik'
import React from 'react'
import { LuPlus, LuTrash2 } from 'react-icons/lu'
import * as yup from 'yup'

import TextInput from '@/components/form/TextInput'
import { SchedulerParticipant } from '@/components/integration/nylas/scheduler/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Heading } from '@/components/ui/heading'

interface FormType extends SchedulerParticipant {
  guests: SchedulerParticipant[]
}

interface Props {
  primaryParticipant: SchedulerParticipant
  guests: SchedulerParticipant[]
  onSubmit: (newData: FormType) => void
  onHide: () => void
}

const ParticipantsModal: React.FC<Props> = ({
  onSubmit,
  onHide,
  primaryParticipant,
  guests,
}) => {
  return (
    <Dialog open onHide={onHide}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Calendar invite details</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={{
            name: primaryParticipant.name,
            email: primaryParticipant.email,
            guests,
          }}
          onSubmit={onSubmit}
          validationSchema={yup.object().shape({
            name: yup.string().required(),
            email: yup.string().email().required(),
            guests: yup.array().of(
              yup.object().shape({
                name: yup.string().required(),
                email: yup.string().email().required(),
              }),
            ),
          })}
        >
          {({ values, submitForm }) => (
            <Form>
              <DialogBody className="pb-4">
                <p className="mb-4">
                  Which email address would you like the calendar invite to be
                  sent to?
                </p>

                <div className="mb-6 flex justify-stretch gap-3 max-md:flex-col">
                  <TextInput label="Name" name="name" placeholder="Name" />

                  <TextInput
                    label="Email"
                    name="email"
                    placeholder="Email"
                    type="email"
                  />
                </div>

                {values.guests.length > 0 && (
                  <Heading size={5} className="text-dark font-bold">
                    Additional guests
                  </Heading>
                )}

                <FieldArray name="guests">
                  {(arrayHelpers) => (
                    <div>
                      {values.guests.map((_value, i) => {
                        return (
                          <fieldset
                            key={i}
                            className="pop-in mb-4 flex items-end
                              justify-stretch gap-3 max-md:flex-col"
                          >
                            <TextInput
                              label="Name"
                              placeholder="Name"
                              name={`guests.${i}.name`}
                            />

                            <TextInput
                              label="Email"
                              placeholder="Email"
                              type="email"
                              name={`guests.${i}.email`}
                            />

                            <div className="flex justify-end">
                              <Button
                                onClick={() => arrayHelpers.remove(i)}
                                variant="ghost"
                                size="icon"
                              >
                                <LuTrash2 />

                                <span className="sr-only">Remove</span>
                              </Button>
                            </div>
                          </fieldset>
                        )
                      })}

                      <div className="flex justify-center">
                        <Button
                          variant="link"
                          onClick={() =>
                            arrayHelpers.push({ name: '', email: '' })
                          }
                        >
                          <LuPlus /> Add a guest
                        </Button>
                      </div>
                    </div>
                  )}
                </FieldArray>
              </DialogBody>

              <DialogFooter>
                <div>
                  <Button
                    size="lg"
                    onClick={() => submitForm()}
                    variant="success"
                  >
                    Save
                  </Button>
                </div>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default ParticipantsModal
